import type {
    CellFocusedEvent,
    CellKeyDownEvent,
    CellPosition,
    ColDef,
    RowClickedEvent,
    TabToNextCellParams,
} from 'ag-grid-community';
import { AgGridReact, type CustomCellRendererProps } from 'ag-grid-react';
import { useRouter } from 'expo-router';
import { useAtomValue } from 'jotai';
import { Badge } from 'primereact/badge';
import { TabPanel, TabView } from 'primereact/tabview';
import React, {
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Linking } from 'react-native';
import {
    BodySmallRegular,
    ButtonSize,
    ButtonVariant,
    H2Regular,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import {
    GuideChipCellRenderer,
    MemberInfoCellRenderer,
    MembershipActiveCellRenderer,
    MembershipTypeCellRenderer,
    MonospaceCellRenderer,
    MonospaceMemberInfoCellRenderer,
    TenureCellRenderer,
} from '../../../common/components/GridCellRenderers';
import { Button } from '../../../common/components/GroveButtons';
import { FullPageContainer } from '../../../common/components/GroveContainers';
import { SearchInput } from '../../../common/components/SearchInput';
import type { BadgeSeverity } from '../../../common/types/Badge';
import { IS_LOCAL } from '../../../config/FruitfulEnvironment';
import { TrackedEvent } from '../../../logger/Events';
import { trackEvent } from '../../../logger/Logger';
import {
    adminUserAtom,
    allGuidesAtom,
    allMembershipsAtom,
    isLoadingMembershipsAtom,
    myMembershipsAtom,
} from '../../entityAtoms';
import { AdminUserRole, type MembershipSummary } from '../../entityTypes';
import { MemberJourneyStatus } from '../dto/MembershipDto'; // Utility function for comparing session date strings
import {
    exportMembersToCsv,
    getTimestampedCsvFileName,
} from '../utils/exportMembersCsv';

// Utility function for comparing session date strings
const sessionDateComparator = (valueA: string, valueB: string): number => {
    // Handle 'N/A' values - treat them as null dates
    if (valueA === 'N/A' && valueB === 'N/A') return 0;
    if (valueA === 'N/A') return 1; // N/A goes to end
    if (valueB === 'N/A') return -1; // N/A goes to end

    // Parse dates in MM/dd/yy format
    const dateA = new Date(valueA);
    const dateB = new Date(valueB);

    // If dates are invalid, fall back to string comparison
    if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
        return valueA.localeCompare(valueB);
    }

    return dateA.getTime() - dateB.getTime();
};

// Utility function for sorting Member Journey Status.
export const memberJourneyStatusComparator = (
    valueA: MemberJourneyStatus,
    valueB: MemberJourneyStatus,
): number => {
    const order = [
        MemberJourneyStatus.PRE_DISCOVERY_TASKS,
        MemberJourneyStatus.DISCOVERY_BOOKED,
        MemberJourneyStatus.PRE_MONEY_MAP_TASKS,
        MemberJourneyStatus.MONEY_MAP_BOOKED,
        MemberJourneyStatus.AWAITING_MONEY_MAP,
        MemberJourneyStatus.RECEIVED_MONEY_MAP,
        MemberJourneyStatus.ACCEPTED_MONEY_MAP,
        MemberJourneyStatus.MOVED_DIRECT_DEPOSIT,
    ];

    const indexA = order.indexOf(valueA);
    const indexB = order.indexOf(valueB);

    // If either value isn't found, move it to the end
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
};

interface GridInterfaceProps {
    onNavigateFromGrid?: () => void;
}

export const AllMembershipsGrid = ({
    onNavigateFromGrid,
}: GridInterfaceProps) => {
    const allMemberships = useAtomValue(allMembershipsAtom);
    return (
        <MembershipsGrid
            variant={MembershipGridVariant.ALL}
            title="All Memberships"
            memberships={allMemberships}
            onNavigateFromGrid={onNavigateFromGrid}
        />
    );
};

export const MyMembershipsGrid = ({
    onNavigateFromGrid,
}: GridInterfaceProps) => {
    const myMemberships = useAtomValue(myMembershipsAtom);
    return (
        <MembershipsGrid
            variant={MembershipGridVariant.MY}
            title="My Memberships"
            memberships={myMemberships}
            onNavigateFromGrid={onNavigateFromGrid}
        />
    );
};

export const GuideMembershipsList = () => {
    return (
        <FullPageContainer>
            <TabView
                style={{ width: '100%', marginTop: -10 }}
                panelContainerStyle={{
                    width: '100%',
                    padding: 0,
                    marginTop: 32,
                }}
            >
                <TabPanel header="My Memberships">
                    <MyMembershipsGrid />
                </TabPanel>
                <TabPanel header="All Memberships">
                    <AllMembershipsGrid />
                </TabPanel>
            </TabView>
        </FullPageContainer>
    );
};

enum MembershipGridVariant {
    ALL = 'ALL',
    MY = 'MY',
}

const MembershipsGrid = ({
    title,
    memberships,
    onNavigateFromGrid,
    variant,
}: {
    title: string;
    memberships: MembershipSummary[];
    onNavigateFromGrid?: () => void;
    variant: MembershipGridVariant;
}) => {
    const router = useRouter();
    const gridRef = useRef<AgGridReact<MembershipSummary>>(null);
    const [searchString, setSearchString] = useState('');

    const adminUser = useAtomValue(adminUserAtom);
    const isLoadingMemberships = useAtomValue(isLoadingMembershipsAtom);
    const guides = useAtomValue(allGuidesAtom);
    const guideMap = useMemo(() => {
        return new Map(guides.map((guide) => [guide.id, guide]));
    }, [guides]);

    const getRowData = (
        memberships: MembershipSummary[],
        activeMembershipsOnly: boolean,
    ) => {
        return activeMembershipsOnly
            ? memberships.filter((m) => m.isMembershipActive)
            : memberships;
    };

    const [activeMembershipsOnly, setActiveMembershipsOnly] = useState(true);
    const [rowData, setRowData] = useState(
        getRowData(memberships, activeMembershipsOnly),
    );

    useEffect(() => {
        const gridApi = gridRef.current?.api;

        setRowData(getRowData(memberships, activeMembershipsOnly));
        gridApi?.setColumnsVisible(
            ['isMembershipActive'],
            !activeMembershipsOnly,
        );
    }, [memberships, activeMembershipsOnly]);

    const GuideCellRenderer = useCallback(
        ({
            data,
        }: CustomCellRendererProps<MembershipSummary, string, unknown>) => {
            const guide = guideMap.get(data?.guideId ?? '');
            return <GuideChipCellRenderer guide={guide} />;
        },
        [guideMap],
    );

    const isAllVariant = variant === MembershipGridVariant.ALL;
    const isMyVariant = variant === MembershipGridVariant.MY;
    const canExport =
        adminUser.role === AdminUserRole.MEMBER_OPS ||
        adminUser.role === AdminUserRole.SUPERADMIN;

    const colDefs = useMemo(
        () =>
            [
                {
                    field: 'isMembershipActive',
                    headerName: 'Active?',
                    width: 90,
                    cellRenderer: MembershipActiveCellRenderer,
                    hide: activeMembershipsOnly,
                },
                {
                    field: 'guideId',
                    headerName: 'Guide',
                    cellStyle: { padding: 0 },
                    cellRenderer: GuideCellRenderer,
                    hide: !isAllVariant,
                },
                {
                    field: 'membersName',
                    headerName: 'Members',
                    cellRenderer: MemberInfoCellRenderer,
                    minWidth: 180,
                },
                {
                    field: 'membersEmail',
                    headerName: 'Email',
                    cellRenderer: MemberInfoCellRenderer,
                },
                {
                    field: 'membershipType',
                    headerName: 'Joint?',
                    cellRenderer: MembershipTypeCellRenderer,
                    minWidth: 90, // so it doesn't get cut off
                    maxWidth: 90, // or waste space
                    hide: isMyVariant,
                },
                {
                    field: 'createdAt',
                    headerName: 'Tenure',
                    cellRenderer: TenureCellRenderer,
                    minWidth: 120, // so it doesn't get cut off
                    maxWidth: 120, // or waste space
                    sort: IS_LOCAL ? 'desc' : undefined,
                },
                {
                    field: 'stripeProductName',
                    headerName: 'Subscription Type',
                    cellRenderer: MonospaceCellRenderer,
                    width: 180,
                    hide: !isMyVariant,
                },
                {
                    field: 'memberJourneyStatus',
                    headerName: 'Member Journey Status',
                    width: 240,
                    cellRenderer: MemberJourneyStatusCellRenderer,
                    hide: !isMyVariant,
                    comparator: memberJourneyStatusComparator,
                    filter: true,
                },
                {
                    field: 'nextSession',
                    headerName: 'Next Session',
                    width: 160,
                    cellRenderer: MonospaceMemberInfoCellRenderer,
                    comparator: sessionDateComparator,
                },
                {
                    field: 'previousSession',
                    headerName: 'Previous Session',
                    width: 160,
                    cellRenderer: MonospaceMemberInfoCellRenderer,
                    comparator: sessionDateComparator,
                },
                {
                    field: 'id',
                    headerName: 'Membership id',
                    cellRenderer: MonospaceCellRenderer,
                    hide: isMyVariant,
                },
                {
                    field: 'membersId',
                    headerName: 'Member id',
                    cellRenderer: MonospaceMemberInfoCellRenderer,
                    hide: isMyVariant,
                },
                {
                    field: 'hasIM',
                    headerName: 'Has IM?',
                    cellRenderer: MonospaceCellRenderer,
                    minWidth: 120, // so it doesn't get cut off
                    maxWidth: 120, // or waste space
                    hide: !isAllVariant,
                },
                {
                    field: 'membersPhone',
                    headerName: 'Phone',
                    hide: !isAllVariant,
                },
                {
                    field: 'membersState',
                    headerName: 'State',
                    cellRenderer: MemberInfoCellRenderer,
                    minWidth: 72,
                    maxWidth: 100,
                },
            ] as Array<ColDef<MembershipSummary>>,
        [isAllVariant, isMyVariant, GuideCellRenderer],
    );

    const onFilterTextBoxChange = useCallback((value: string) => {
        const gridApi = gridRef.current?.api;

        gridApi?.setGridOption('quickFilterText', value);
        setSearchString(value);
        // auto-select the first row so the 'enter' key will navigate to that membership
        gridApi?.getDisplayedRowAtIndex(0)?.setSelected(true);
    }, []);

    const onFilterTextBoxSubmit = useCallback(() => {
        const gridApi = gridRef.current?.api;
        const selectedRow = gridApi?.getSelectedRows()[0];

        goToMembershipPage(selectedRow?.id);
    }, []);

    const onFilterTextBoxBlur = useCallback(() => {
        const gridApi = gridRef.current?.api;

        searchString === '' && gridApi?.deselectAll();
    }, [searchString]);

    const clearSearch = useCallback(() => {
        const gridApi = gridRef.current?.api;

        setSearchString('');
        gridApi?.setGridOption('quickFilterText', undefined);
        gridApi?.deselectAll();
    }, []);

    const onRowClicked = useCallback(
        (event: RowClickedEvent<MembershipSummary>) => {
            const membershipId = event.data?.id ?? null;
            const selection = document.getSelection();
            const hasNoSelectedText = selection?.type !== 'Range';

            if (membershipId !== null && hasNoSelectedText) {
                const mouseEvent = event.event as MouseEvent | null;

                if (mouseEvent?.shiftKey ?? mouseEvent?.metaKey) {
                    const newUrl = `/membership/${membershipId}`;
                    void Linking.openURL(newUrl);
                    onNavigateFromGrid?.();
                } else {
                    goToMembershipPage(membershipId);
                }
            }
        },
        [],
    );

    // Receives an event for "tab to next cell" (because by default, the grid implements tabbing through cells)
    // and turns that into tabbing through rows instead.
    const handleTabForRowSelection = useCallback(
        ({
            api,
            backwards,
            previousCellPosition,
        }: TabToNextCellParams): CellPosition | null => {
            const nextIndex =
                (backwards ? -1 : 1) + previousCellPosition.rowIndex;
            const clampedNextIndex = Math.min(
                Math.max(nextIndex, -1), // no less than -1
                api.getDisplayedRowCount() - 1, // no more than count of displayed rows
            );

            return {
                rowIndex: clampedNextIndex,
                column: previousCellPosition.column,
                rowPinned: previousCellPosition.rowPinned,
            };
        },
        [],
    );

    const onCellFocused = useCallback(({ api, rowIndex }: CellFocusedEvent) => {
        rowIndex !== null &&
            api.getDisplayedRowAtIndex(rowIndex)?.setSelected(true);
    }, []);

    const onCellKeyDown = useCallback(
        ({ data, event }: CellKeyDownEvent<MembershipSummary>) => {
            if ((event as KeyboardEvent)?.key === 'Enter') {
                goToMembershipPage(data?.id);
            }
        },
        [],
    );

    const goToMembershipPage = useCallback(
        (membershipId: string | null | undefined) => {
            if (membershipId != null) {
                router.navigate({
                    pathname: '/membership/[id]',
                    params: { id: membershipId },
                });
                onNavigateFromGrid?.();
                clearSearch();
            }
        },
        [],
    );

    const trackMembershipListExport = useCallback(
        (event: TrackedEvent) => {
            const gridApi = gridRef.current?.api;

            trackEvent(event, {
                variant,
                activeMembershipsOnly,
                displayedMembershipCount: gridApi?.getDisplayedRowCount() ?? 0,
            });
        },
        [variant, activeMembershipsOnly],
    );

    if (isLoadingMemberships) {
        return (
            <YStack
                width="100%"
                paddingVertical="25vh"
                justifyContent="center"
                alignItems="center"
                gap={8}
            >
                <Spinner />
                <BodySmallRegular textAlign="center" color="$gray1000">
                    Loading memberships...
                </BodySmallRegular>
            </YStack>
        );
    }

    return (
        <YStack width="100%" gap={16}>
            <XStack
                width="100%"
                justifyContent="space-between"
                alignItems="center"
            >
                <H2Regular>{title}</H2Regular>
                <SearchInput
                    value={searchString}
                    placeholder="Name, email, phone, id..."
                    onChangeText={onFilterTextBoxChange}
                    onSubmitEditing={onFilterTextBoxSubmit}
                    onBlur={onFilterTextBoxBlur}
                />
            </XStack>

            <div
                className="ag-theme-material"
                style={{
                    height: '60vh',
                    width: '100%',
                }}
            >
                {/* TODO move these to a stylesheet once we know what will be common */}
                <style type="text/css">{`
                        .ag-theme-material {
                            --ag-row-height: 60px;
                            --ag-row-hover-color: #E1FDEA;
                            --ag-selected-row-background-color: #E1FDEA;
                        }
                        .ag-theme-material .ag-row:hover {
                            cursor: pointer;
                        }
                        .ag-cell.ag-cell-normal-height .ag-cell-wrapper {
                            height: 100%;
                        }
                        /* is_SizableText is a tamagui text class, and we want to override its pre-wrap value to enable ellipsis */
                        .ag-cell .ag-cell-wrapper .is_SizableText {
                            white-space: nowrap;
                        }
                        .ag-cell.ag-cell-focus:not(.ag-cell-range-selected):focus-within {
                            border-width: 0;
                        }
                        .ag-cell.ag-cell-focus:focus-within:first-child {
                            border-left-width: 2px;
                            border-color: #10A45F;
                        }
                    `}</style>
                <AgGridReact<MembershipSummary>
                    ref={gridRef}
                    rowData={rowData}
                    columnDefs={colDefs}
                    autoSizeStrategy={
                        isAllVariant ? undefined : { type: 'fitGridWidth' } // Allow horizontal scroll for All variant.
                    }
                    autoSizePadding={12}
                    suppressHeaderFocus={true}
                    enableCellTextSelection={true}
                    ensureDomOrder={true}
                    includeHiddenColumnsInQuickFilter={true}
                    tabToNextCell={handleTabForRowSelection}
                    onCellFocused={onCellFocused}
                    onCellKeyDown={onCellKeyDown}
                    onRowClicked={onRowClicked}
                />
            </div>
            <XStack alignItems="center" gap={16}>
                <BodySmallRegular>
                    {rowData.length ?? 0} memberships
                </BodySmallRegular>
                <BodySmallRegular>|</BodySmallRegular>
                <XStack flex={1} justifyContent="space-between">
                    <Button
                        variant={ButtonVariant.TERTIARY}
                        size={ButtonSize.SMALL}
                        onPress={() => {
                            setActiveMembershipsOnly((prev) => {
                                return !prev;
                            });
                        }}
                    >
                        {activeMembershipsOnly
                            ? 'Include inactive memberships'
                            : 'Hide inactive memberships'}
                    </Button>
                    {canExport && (
                        <XStack gap={8}>
                            <Button
                                variant={ButtonVariant.SECONDARY}
                                size={ButtonSize.TINY}
                                onPress={() => {
                                    trackMembershipListExport(
                                        TrackedEvent.MembershipsExportedToCsv,
                                    );
                                    const gridApi = gridRef.current?.api;

                                    gridApi?.exportDataAsCsv({
                                        fileName:
                                            getTimestampedCsvFileName(
                                                'memberships-export',
                                            ),
                                    });
                                }}
                            >
                                Export Memberships to CSV
                            </Button>
                            <Button
                                variant={ButtonVariant.SECONDARY}
                                size={ButtonSize.TINY}
                                onPress={() => {
                                    const gridApi = gridRef.current?.api;
                                    if (gridApi == null) {
                                        return;
                                    }

                                    trackMembershipListExport(
                                        TrackedEvent.MembersExportedToCsv,
                                    );
                                    exportMembersToCsv({ gridApi });
                                }}
                            >
                                Export Members to CSV
                            </Button>
                        </XStack>
                    )}
                </XStack>
            </XStack>
        </YStack>
    );
};

export const MemberJourneyStatusCellRenderer = memo(
    ({
        value,
    }: CustomCellRendererProps<MembershipSummary, string, unknown>) => {
        // Loosely mapping severity to current inhibitor to member journey progression. Danger = Guide; Warning = Member, None = Info.
        const getSeverity = (): BadgeSeverity => {
            switch (value) {
                case MemberJourneyStatus.PRE_DISCOVERY_TASKS:
                    return 'warning';
                case MemberJourneyStatus.DISCOVERY_BOOKED:
                    return 'info';
                case MemberJourneyStatus.PRE_MONEY_MAP_TASKS:
                    return 'warning';
                case MemberJourneyStatus.MONEY_MAP_BOOKED:
                    return 'info';
                case MemberJourneyStatus.AWAITING_MONEY_MAP:
                    return 'danger';
                case MemberJourneyStatus.RECEIVED_MONEY_MAP:
                    return 'warning';
                case MemberJourneyStatus.ACCEPTED_MONEY_MAP:
                    return 'info';
                case MemberJourneyStatus.MOVED_DIRECT_DEPOSIT:
                    return 'success';
                default:
                    return 'info';
            }
        };

        return <Badge value={value} severity={getSeverity()} />;
    },
);
